<template>
  <v-navigation-drawer
    v-model="state.open"
    right
    temporary
    app
    class="current-pay-plan-drawer-connected"
    width="min(1100px, 92vw)"
  >
    <div
      v-if="state.error"
      class="cppdc__error"
      data-testid="cppdc-inline-error"
      role="alert"
    >
      {{ state.error }}
    </div>

    <!-- ECL design component (ER-12118); its own Loading variant covers fetch -->
    <CurrentPayPlanDrawer
      v-else-if="version || state.loading"
      :loading="state.loading"
      :version="drawerVersion"
      :fair-pay-at-effective-date="fairPayAtEffectiveDate"
      :readiness-enabled="readinessEnabled"
      :range-penetration="rangePenetration"
      :future-plan-code="futurePlanCode"
      :future-plan-date="futurePlanDate"
      :voidable="voidable"
      :void-blocked-by="voidBlockedBy"
      :terminable="terminable"
      @propose-changes="emit('propose-changes', { versionId: state.versionId, version })"
      @terminate="openTerminate"
      @void="openVoid"
      @open-chatter="
        emit('open-chatter', { id: state.versionId, versionCode: version?.code })
      "
      @close="close"
    />

    <!-- Terminate confirmation (Rule A-7: date + reason required) -->
    <PayPlanDialog
      :open="state.terminateOpen"
      title="Terminate pay plan"
      :submitting="state.submitting"
      @close="cancelTerminate"
    >
      <template #icon>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path
            d="M10 6.667v3.333m0 3.333h.008M8.575 2.283 1.517 14.167a1.667 1.667 0 0 0 1.425 2.5h14.116a1.667 1.667 0 0 0 1.425-2.5L11.425 2.283a1.667 1.667 0 0 0-2.85 0Z"
            stroke="#d92d20"
            stroke-width="1.67"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </template>

      <template #description>
        Terminating <strong>{{ version?.code || "this version" }}</strong> ends the plan in
        force. This cannot be undone.
      </template>

      <label class="cppdc__dialog-label" for="cppdc-terminate-date">
        Termination date
      </label>
      <div class="cppdc__date-row">
        <button
          id="cppdc-terminate-date"
          type="button"
          class="cppdc__date-btn"
          data-testid="cppdc-terminate-date"
          @click="datePickerOpen = true"
        >
          {{ state.terminateDate || "Select a date" }}
        </button>
      </div>
      <DatePicker
        v-if="datePickerOpen"
        :is-date-picker-open="datePickerOpen"
        :value="state.terminateDate"
        @on-apply-date="handleTerminateDate"
        @on-close="datePickerOpen = false"
      />

      <label class="cppdc__dialog-label" for="cppdc-terminate-reason">
        Reason
      </label>
      <v-textarea
        id="cppdc-terminate-reason"
        v-model="state.terminateReason"
        data-testid="cppdc-terminate-reason"
        rows="3"
        auto-grow
        hide-details
        outlined
        dense
      />

      <template #footer>
        <StandardButton
          type="magenta"
          variant="outline"
          size="normal"
          label="Cancel"
          data-testid="cppdc-terminate-cancel"
          :disabled="state.submitting"
          @click="cancelTerminate"
        />
        <StandardButton
          type="red"
          variant="primary"
          size="normal"
          label="Terminate"
          data-testid="cppdc-terminate-confirm"
          :disabled="!state.terminateReason.trim() || state.submitting || featureDisabled"
          :loader-left="state.submitting"
          @click="terminateVersion"
        />
      </template>
    </PayPlanDialog>

    <!-- Void confirmation (Rule B-14: irreversible, reason required) -->
    <PayPlanDialog
      :open="state.voidOpen"
      title="Void pay plan version"
      :submitting="state.submitting"
      @close="cancelVoid"
    >
      <template #icon>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path
            d="M10 6.667v3.333m0 3.333h.008M8.575 2.283 1.517 14.167a1.667 1.667 0 0 0 1.425 2.5h14.116a1.667 1.667 0 0 0 1.425-2.5L11.425 2.283a1.667 1.667 0 0 0-2.85 0Z"
            stroke="#d92d20"
            stroke-width="1.67"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </template>

      <template #description>
        This action is irreversible. Voiding
        <strong>{{ version?.code || "this version" }}</strong> permanently voids this pay plan
        version. A reason is required.
      </template>

      <label class="cppdc__dialog-label" for="cppdc-void-reason">Reason</label>
      <v-textarea
        id="cppdc-void-reason"
        v-model="state.voidReason"
        data-testid="cppdc-void-reason"
        rows="3"
        auto-grow
        hide-details
        outlined
        dense
      />

      <template #footer>
        <StandardButton
          type="magenta"
          variant="outline"
          size="normal"
          label="Cancel"
          data-testid="cppdc-void-cancel"
          :disabled="state.submitting"
          @click="cancelVoid"
        />
        <StandardButton
          type="red"
          variant="primary"
          size="normal"
          label="Void"
          data-testid="cppdc-void-confirm"
          :disabled="!state.voidReason.trim() || state.submitting || featureDisabled"
          :loader-left="state.submitting"
          @click="voidVersion"
        />
      </template>
    </PayPlanDialog>
  </v-navigation-drawer>
</template>

<script setup>
import { computed, ref } from "vue";
import CurrentPayPlanDrawer from "@components/CurrentPayPlanDrawer/CurrentPayPlanDrawer.vue";
import DatePicker from "@components/DatePicker/DatePicker.vue";
import PayPlanDialog from "@components/PayPlanDialog/PayPlanDialog.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import { useCurrentPayPlanDrawer } from "./composables/useCurrentPayPlanDrawer";

const props = defineProps({
  proxy: {
    type: Object,
    default: null,
  },
  fetchRows: {
    type: Function,
    default: null,
  },
});

const emit = defineEmits(["open-chatter", "propose-changes"]);

const {
  state,
  version,
  fairPayAtEffectiveDate,
  readinessEnabled,
  rangePenetration,
  voidable,
  voidBlockedBy,
  terminable,
  futurePlanCode,
  futurePlanDate,
  featureDisabled,
  open,
  close,
  openTerminate,
  cancelTerminate,
  terminateVersion,
  openVoid,
  cancelVoid,
  voidVersion,
} = useCurrentPayPlanDrawer({ proxy: props.proxy, fetchRows: props.fetchRows });

defineExpose({ open, close });

const datePickerOpen = ref(false);

/** The ECL requires `version`; supply a blank shell while the fetch is running. */
const drawerVersion = computed(
  () =>
    version.value || {
      code: "",
      status: "in-force",
      effective_from: null,
      effective_to: null,
      country: null,
      currency: null,
      amount: null,
      pay_basis: null,
      fte: null,
      employee: "",
      employee_title: "",
      avatar_url: null,
    }
);

function handleTerminateDate(date) {
  if (date) {
    const parsed = new Date(date);
    const year = parsed.getFullYear();
    const month = `${parsed.getMonth() + 1}`.padStart(2, "0");
    const day = `${parsed.getDate()}`.padStart(2, "0");
    state.terminateDate = `${year}-${month}-${day}`;
  }
  datePickerOpen.value = false;
}
</script>

<style scoped>
.cppdc__error {
  margin: 16px;
  padding: 12px 16px;
  border: 1px solid #fda29b;
  border-radius: 8px;
  background: #fef3f2;
  color: #b42318;
  font-size: 14px;
  line-height: 20px;
}

.cppdc__dialog-copy {
  margin: 0 0 12px;
  color: #344054;
  font-size: 14px;
  line-height: 20px;
}

.cppdc__dialog-title {
  color: #101828;
  font-size: 16px;
  font-weight: 600;
}

.cppdc__dialog-label {
  display: block;
  margin: 12px 0 4px;
  color: #344054;
  font-size: 13px;
  font-weight: 500;
}

.cppdc__date-row {
  display: flex;
  gap: 8px;
}

.cppdc__date-btn {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: #ffffff;
  color: #101828;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
}

.cppdc__dialog-actions {
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
}
</style>
